/**
 * What this browser saw, sent back so it can be graphed beside what the
 * server saw.
 *
 * The services measure themselves from inside a handler, which cannot see the
 * network in front of it, the proxy's queue, or the seconds spent parsing two
 * megabytes of script. That gap is the whole argument about whether the
 * terminal is fast: a card route answering in forty milliseconds while the
 * screen takes four seconds is a success by every server-side measure.
 *
 * Deliberately cheap. One beacon when the page has settled and one every
 * quarter minute after it, sent with `sendBeacon` so it never holds anything
 * up and survives the tab being closed.
 */

type PageEntry = { phase: string; ms: number }
type CallEntry = { path: string; status: number; ms: number }

/** Long enough to batch a screen's worth of calls, short enough to be live. */
const FLUSH_EVERY_MS = 15_000

/** A beacon has a size limit, and a page cannot honestly report more. */
const MOST_ENTRIES = 60

let calls: Array<CallEntry> = []
let endpoint: string | undefined
let started = false

const post = (body: { page?: Array<PageEntry>; calls?: Array<CallEntry> }) => {
  if (!endpoint) {
    return
  }
  try {
    const blob = new Blob([JSON.stringify(body)], {
      type: 'application/json',
    })
    // Falls back to fetch where sendBeacon is missing or refuses: it returns
    // false when the queue is full, and losing the sample is fine — this is
    // an instrument, not a ledger.
    if (!navigator.sendBeacon?.(endpoint, blob)) {
      void fetch(endpoint, { method: 'POST', body: blob, keepalive: true })
    }
  } catch {
    // Never let reporting a timing break the screen it is timing.
  }
}

const flush = () => {
  if (calls.length === 0) {
    return
  }
  const batch = calls.slice(0, MOST_ENTRIES)
  calls = []
  post({ calls: batch })
}

/** The milestones the server keeps a series for; anything else is dropped. */
const pageEntries = (): Array<PageEntry> => {
  const [navigation] = performance.getEntriesByType(
    'navigation',
  ) as Array<PerformanceNavigationTiming>
  if (!navigation) {
    return []
  }

  const entries: Array<PageEntry> = [
    { phase: 'ttfb', ms: navigation.responseStart - navigation.startTime },
    {
      phase: 'dom-interactive',
      ms: navigation.domInteractive - navigation.startTime,
    },
    {
      phase: 'dom-content-loaded',
      ms: navigation.domContentLoadedEventEnd - navigation.startTime,
    },
  ]

  if (navigation.loadEventEnd > 0) {
    entries.push({
      phase: 'load',
      ms: navigation.loadEventEnd - navigation.startTime,
    })
  }

  for (const paint of performance.getEntriesByType('paint')) {
    if (paint.name === 'first-contentful-paint') {
      entries.push({ phase: 'first-contentful-paint', ms: paint.startTime })
    }
  }

  return entries.filter((entry) => Number.isFinite(entry.ms) && entry.ms >= 0)
}

/** One API call, as the browser timed it. Called from the fetch wrapper. */
export const recordApiCall = (entry: CallEntry) => {
  if (!started || calls.length >= MOST_ENTRIES * 2) {
    return
  }
  calls.push(entry)
}

/**
 * Starts reporting. Safe to call more than once.
 *
 * `apiBaseUrl` is where the terminal's own API lives, because that is the
 * service holding the histogram; anywhere else and the numbers would arrive
 * somewhere nothing scrapes.
 */
export const startBrowserTiming = (apiBaseUrl: string) => {
  if (started || typeof window === 'undefined') {
    return
  }
  started = true
  endpoint = `${apiBaseUrl.replace(/\/+$/, '')}/api/browser-timing`

  const sendPage = () => {
    const page = pageEntries()
    if (page.length > 0) {
      post({ page })
    }
  }

  // After load, so `loadEventEnd` is a number rather than zero.
  if (document.readyState === 'complete') {
    setTimeout(sendPage, 0)
  } else {
    window.addEventListener('load', () => setTimeout(sendPage, 0), {
      once: true,
    })
  }

  // The largest paint is only known once it stops growing, which the browser
  // signals by the page being hidden or by nothing larger arriving.
  try {
    let largest = 0
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        largest = Math.max(largest, entry.startTime)
      }
    })
    observer.observe({ type: 'largest-contentful-paint', buffered: true })
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'hidden' && largest > 0) {
          post({ page: [{ phase: 'largest-contentful-paint', ms: largest }] })
          largest = 0
        }
      },
      { once: true },
    )
  } catch {
    // Not every browser has it, and the rest of this does not depend on it.
  }

  setInterval(flush, FLUSH_EVERY_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush()
    }
  })
}
